/**
 * DisputeButton Component
 *
 * Shows a dispute button on booking detail pages when eligible.
 * Checks eligibility via API and opens a dispute form modal.
 *
 * API Endpoints:
 *   GET  /api/v1/bookings/:bookingId/can-dispute?cancellationId=...
 *   POST /api/v1/bookings/:bookingId/dispute
 *
 * Story: NEW-005-FE - Dispute Initiation (Customer/Guide UI)
 * MVP: 1.5.2 - Booking Payment Domain Model
 */

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import apiRequest from '../../services/api';
import Modal from '../shared/Modal';
import {
  AlertTriangle,
  CheckCircle,
  AlertCircle,
  Plus,
  Trash2,
  Clock
} from 'lucide-react';

export default function DisputeButton({ bookingId, cancellationId }) {
  const [eligible, setEligible] = useState(false);
  const [eligibilityData, setEligibilityData] = useState(null);
  const [checking, setChecking] = useState(true);
  const [showModal, setShowModal] = useState(false);

  // Form state
  const [reason, setReason] = useState('');
  const [desiredOutcome, setDesiredOutcome] = useState('');
  const [evidenceUrls, setEvidenceUrls] = useState(['']);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  // Check eligibility
  useEffect(() => {
    if (!bookingId || !cancellationId) {
      setChecking(false);
      return;
    }

    const checkEligibility = async () => {
      try {
        setChecking(true);
        const response = await apiRequest(
          `/api/v1/bookings/${bookingId}/can-dispute?cancellationId=${cancellationId}`
        );
        if (response.success && response.data) {
          setEligible(response.data.eligible !== false && response.data.canDispute !== false);
          setEligibilityData(response.data);
        }
      } catch {
        // If the API fails, don't show the button
        setEligible(false);
      } finally {
        setChecking(false);
      }
    };

    checkEligibility();
  }, [bookingId, cancellationId]);

  const handleAddUrl = () => {
    setEvidenceUrls([...evidenceUrls, '']);
  };

  const handleRemoveUrl = (index) => {
    setEvidenceUrls(evidenceUrls.filter((_, i) => i !== index));
  };

  const handleUrlChange = (index, value) => {
    const updated = [...evidenceUrls];
    updated[index] = value;
    setEvidenceUrls(updated);
  };

  const handleSubmit = async () => {
    if (!reason.trim()) {return;}

    try {
      setSubmitting(true);
      setError(null);

      const body = {
        cancellationId,
        reason: reason.trim(),
      };

      if (desiredOutcome.trim()) {
        body.desiredOutcome = desiredOutcome.trim();
      }

      const urls = evidenceUrls.filter((u) => u.trim());
      if (urls.length > 0) {
        body.evidenceUrls = urls;
      }

      const response = await apiRequest(`/api/v1/bookings/${bookingId}/dispute`, {
        method: 'POST',
        body: JSON.stringify(body),
      });

      if (response.success) {
        setResult(response.data);
      }
    } catch (err) {
      setError(err.message || 'Failed to submit dispute');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setShowModal(false);
    // Reset form if dispute was submitted
    if (result) {
      setEligible(false); // Hide button after successful dispute
    }
    setReason('');
    setDesiredOutcome('');
    setEvidenceUrls(['']);
    setError(null);
    setResult(null);
  };

  const getDaysRemaining = () => {
    if (!eligibilityData?.daysRemaining && eligibilityData?.daysRemaining !== 0) {return null;}
    return eligibilityData.daysRemaining;
  };

  // Don't render while checking or if not eligible
  if (checking || !eligible) {return null;}

  const daysRemaining = getDaysRemaining();

  return (
    <>
      {/* Button */}
      <div className="mt-3">
        <button
          onClick={() => setShowModal(true)}
          className="inline-flex items-center space-x-2 px-4 py-2 bg-amber-500 text-white rounded-md hover:bg-amber-600 transition-colors text-sm font-medium"
        >
          <AlertTriangle className="w-4 h-4" />
          <span>Dispute This Decision</span>
        </button>
        {daysRemaining !== null && (
          <p className="mt-1 flex items-center space-x-1 text-xs text-gray-500">
            <Clock className="w-3 h-3" />
            <span>{daysRemaining} day{daysRemaining !== 1 ? 's' : ''} remaining to file a dispute</span>
          </p>
        )}
      </div>

      {/* Dispute Form Modal */}
      <Modal
        isOpen={showModal}
        onClose={handleClose}
        title={result ? 'Dispute Filed' : 'File a Dispute'}
        footer={
          result ? (
            <button
              onClick={handleClose}
              className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition"
            >
              Close
            </button>
          ) : (
            <>
              <button
                onClick={handleClose}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={!reason.trim() || submitting}
                className="flex items-center space-x-2 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed transition"
              >
                <span>{submitting ? 'Submitting...' : 'Submit Dispute'}</span>
              </button>
            </>
          )
        }
        closeOnBackdrop={false}
      >
        {result ? (
          <div className="text-center py-6">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Dispute Filed</h3>
            <p className="text-gray-600">
              Our team will review your case within 48 hours.
              You&apos;ll receive email updates on the progress.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Reason */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Why are you disputing? <span className="text-red-500">*</span>
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Explain why you disagree with the cancellation outcome"
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm resize-none focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              />
            </div>

            {/* Desired Outcome */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                What resolution would you like?
              </label>
              <textarea
                value={desiredOutcome}
                onChange={(e) => setDesiredOutcome(e.target.value)}
                placeholder="Describe your preferred outcome (optional)"
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm resize-none focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              />
            </div>

            {/* Evidence URLs */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Supporting Evidence (optional)
              </label>
              <div className="space-y-2">
                {evidenceUrls.map((url, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input
                      type="url"
                      value={url}
                      onChange={(e) => handleUrlChange(i, e.target.value)}
                      placeholder="https://..."
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                    />
                    {evidenceUrls.length > 1 && (
                      <button
                        onClick={() => handleRemoveUrl(i)}
                        className="p-1.5 text-gray-400 hover:text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  onClick={handleAddUrl}
                  className="flex items-center space-x-1 text-xs text-amber-600 hover:text-amber-700"
                >
                  <Plus className="w-3 h-3" />
                  <span>Add URL</span>
                </button>
              </div>
            </div>

            {/* Error */}
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-2">
                <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                <span className="text-sm text-red-700">{error}</span>
              </div>
            )}
          </div>
        )}
      </Modal>
    </>
  );
}

DisputeButton.propTypes = {
  bookingId: PropTypes.string.isRequired,
  cancellationId: PropTypes.string.isRequired,
};
